// @ts-check
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Redirect } from 'react-router';
import { Box } from '~/design-system';
import DefaultImportStateErrorPage from '../ImportComplete/DefaultImportStateErrorPage';
import mergeCustomQuestions from '../mergeCustomQuestions';
import FieldsTableErrors from './FieldTableErrors';
import FieldsTableValidation from './FieldsTableValidation';
import ValidationSkeleton from './ValidationSkeleton';

/**
 * The props that this component needs.
 * @typedef {object} ValidateImportStepProps
 * @property {string} [props.redirectToIfValid] - the link to redirect if there is no errors
 * @property {Object[]} props.masterListColumns - the master list columns to this import
 */

/**
 * Renders the page where import is validated
 * @param {ReturnType<useValidation> & ValidateImportStepProps} props
 */
export default function Validation({
  error,
  importData,
  isValid,
  masterListColumns,
  loading,
  redirectToIfValid,
  ...rest
}) {
  const [columnErrorSelected, setColumnErrorSelected] = React.useState(null);
  if (error)
    return (
      <DefaultImportStateErrorPage importData={importData} error={error} />
    );
  if (loading) return <ValidationSkeleton />;
  if (isValid && redirectToIfValid) return <Redirect to={redirectToIfValid} />;
  const { sourceFields, bottlebooksFields, dataRowIndex } = importData;
  const deletedQuestions = getDeletedQuestions(bottlebooksFields);
  //Deleted questions must be included in masterListColumns and bottlebooksFieldsMap here, so we display the error.
  masterListColumns = [...masterListColumns, ...deletedQuestions];

  return (
    <Box
      sx={{
        textAlign: 'center',
        marginTop: 2,
        padding: '0px 15% 30px 15%',
        marginBottom: 6,
      }}
      {...rest}
    >
      <FieldsTableValidation
        sourceFields={sourceFields}
        bottlebooksFields={bottlebooksFields}
        onShowColErrors={(col) => setColumnErrorSelected(col)}
        masterListColumns={masterListColumns}
        isValid={isValid}
      />
      {!isValid && columnErrorSelected && (
        <FieldsTableErrors
          key={columnErrorSelected.key}
          sx={{ marginTop: 5 }}
          sourceFields={sourceFields}
          bottlebooksFields={bottlebooksFields}
          column={columnErrorSelected}
          dataRowIndex={dataRowIndex}
          masterListColumns={masterListColumns}
        />
      )}
    </Box>
  );
}

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {Object} props
 * @param {String} props.workspaceId
 * @param {String} props.importId
 * @param {object[]} [props.fieldsToImport]
 * @param {object[]} [props.bottlebooksFieldsSource]
 * @returns
 */
export function useValidation({
  workspaceId,
  importId,
  fieldsToImport,
  bottlebooksFieldsSource,
}) {
  const { data, loading, error } = useQuery(QUERY, {
    variables: { workspaceId, importId },
    fetchPolicy: 'network-only',
  });

  return {
    error,
    loading,
    fieldsToImport,
    bottlebooksFieldsSource,
    ...afterQuery({ data, bottlebooksFieldsSource, fieldsToImport }),
  };
}

function afterQuery({ data, bottlebooksFieldsSource, fieldsToImport = [] }) {
  const _importData = data?.workspace?.dataImport || {};
  const importData = {
    ..._importData,
    bottlebooksFields: (_importData.bottlebooksFields || []).map(
      (bottlebooksField) => {
        const errors = (_importData.validationErrors || [])
          .filter(
            ({ bottlebooksFieldName }) =>
              bottlebooksFieldName === bottlebooksField.bottlebooksFieldName
          )
          .map(({ errors }) => errors)
          .flat();
        return {
          ...bottlebooksField,
          errors,
        };
      }
    ),
  };

  const isValid = !Boolean(
    importData?.bottlebooksFields?.find(({ errors }) => errors?.length > 0)
  );
  //Add custom fields into bottlebooksFieldsSource
  const mergedBottlebooksFieldsSource = mergeCustomQuestions(
    bottlebooksFieldsSource,
    data?.workspace?.configuration?.customProductFields
  );
  return {
    importData,
    isValid,
    masterListColumns: [...fieldsToImport, ...mergedBottlebooksFieldsSource],
  };
}

function getDeletedQuestions(bottlebooksFields = []) {
  return bottlebooksFields.reduce((acc, bottlebooksField) => {
    const anyDeletedError = bottlebooksField.errors.some(
      (error) => error.code === 'question_not_found'
    );
    if (anyDeletedError)
      acc.push({
        key: bottlebooksField.bottlebooksFieldName,
        label: bottlebooksField.importFieldName,
        required: false,
      });
    return acc;
  }, []);
}

const QUERY = gql`
  query Validation($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      configuration {
        workspaceId
        customProductFields {
          label
          key
          type
        }
      }
      dataImport(importId: $importId) {
        importId
        dataRowIndex
        bottlebooksFields {
          bottlebooksFieldName
          importFieldName
        }
        validationErrors {
          bottlebooksFieldName
          errors {
            index
            code
            value
            relatedFields {
              bottlebooksFieldName
              value
            }
          }
        }
      }
    }
  }
`;
